import * as React from 'react';
import { connect } from 'react-redux';
import { MuiThemeProvider, Theme } from '@material-ui/core';

import { GlobalState } from 'ha/types/store';
import { asyncConnect, AsyncItems } from 'ha/modules/ReduxConnect';
import { HOME } from 'ha/constants/pageNames';
import Page from 'ha/modules/Page';
import { load as loadFeaturedCities } from 'ha/modules/FeaturedCities/actions';
import loadSavedSearches from 'ha/modules/SavedSearches/actions/loadSavedSearches';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import { defaultFont } from 'ha/constants/FontInfo';
import { loadHermesData } from 'ha/modules/Hermes';

import HomeLayout from '../components/HomeLayout';
import { homeNextFont, TopCitiesNameList } from '../constants';
import { getCities, getCounts } from '../actions';
import { getCities as getCitiesSelector } from '../selectors';
import getCardBlogs from '../selectors/getCardBlogs';
import { Blog } from '../types';

const asyncPageItems: AsyncItems<{}, GlobalState> = [
  {
    key: 'home',
    promise: ({
      store: { dispatch, getState },
      intl: {
        urlResolver: { getLandlordsCommunityUrl, hasLandlordsCommunityUrl },
        lang,
      },
    }) => {
      const state = getState();
      const userId = getUserId(state);

      return Promise.all([
        userId
          ? dispatch(loadSavedSearches(userId, { language: lang }))
          : Promise.resolve(),
        dispatch(loadFeaturedCities()),
        dispatch(getCities(TopCitiesNameList, lang)).then(() => {
          const cities = getCitiesSelector(getState());
          return dispatch(getCounts(cities));
        }),
        hasLandlordsCommunityUrl(lang)
          ? dispatch(loadHermesData(getLandlordsCommunityUrl(lang)))
          : Promise.resolve(null),
      ]);
    },
  },
];

const homeNextFontFamily = `'${homeNextFont.fontName}', 'Helvetica', 'Arial', 'sans-serif'`;

// to avoid new theme creation each rerender
const homeNextTheme: (outerTheme: Theme) => Theme = outerTheme => ({
  ...outerTheme,
  typography: {
    ...outerTheme.typography,
    display1: {
      ...outerTheme.typography.display1,
      fontFamily: homeNextFontFamily,
    },
    headline: {
      ...outerTheme.typography.headline,
      fontFamily: homeNextFontFamily,
    },
    title: {
      ...outerTheme.typography.title,
      fontFamily: homeNextFontFamily,
    },
  },
});

interface Props {
  blogs: Blog[];
}

const HomePageContainer: React.SFC<Props> = ({ blogs }) => (
  <Page
    name={HOME}
    isLandingPage
    noFollowFooter={false}
    activeFonts={[defaultFont, homeNextFont]}
    remarketingTag={{
      hrental_id: '',
      hrental_pagetype: 'homepage',
      hrental_startdate: '',
      hrental_enddate: '',
      hrental_totalvalue: '',
    }}
  >
    <MuiThemeProvider theme={homeNextTheme}>
      <HomeLayout blogs={blogs} />
    </MuiThemeProvider>
  </Page>
);

const mapStateToProps = (state: GlobalState) => ({
  blogs: getCardBlogs(state),
});

export default asyncConnect(asyncPageItems)(
  connect(mapStateToProps)(HomePageContainer),
);
